import { createContext, useContext, useMemo, type CSSProperties, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'

import { useSelectedBackendServerUrl } from '@/lib/backend-servers'
import { orgTreeApi, type OrgTreeServiceInstance } from '@/services/org-tree'

type ThemeFallbackConfig = {
  enabled?: boolean
  organisation_id?: string | number | null
  organization_id?: string | number | null
  primary_color?: string | null
  allowed_hosts?: string[]
}

type AuthThemeState = {
  status: 'loading' | 'ready' | 'unconfigured' | 'error'
  source: 'domain' | 'fallback' | 'none'
  host: string
  locked: boolean
  organization: OrgTreeServiceInstance | null
  primaryColor: string | null
  message: string | null
}

const AuthThemeContext = createContext<AuthThemeState>({
  status: 'loading',
  source: 'none',
  host: '',
  locked: false,
  organization: null,
  primaryColor: null,
  message: null,
})

function normalizeHost(value: unknown): string {
  const rawValue = String(value ?? '').trim().toLowerCase()
  if (!rawValue) {
    return ''
  }

  try {
    const parsed = new URL(rawValue.includes('://') ? rawValue : `http://${rawValue}`)
    return parsed.hostname.replace(/\.$/, '')
  } catch {
    return rawValue.split('/')[0].split(':')[0].replace(/\.$/, '')
  }
}

function isLocalHost(host: string): boolean {
  return host === 'localhost'
    || host === '127.0.0.1'
    || host === '0.0.0.0'
    || host === '::1'
    || host.endsWith('.localhost')
}

function normalizePrimaryColor(value: unknown): string | null {
  const rawValue = String(value ?? '').trim()
  if (!rawValue) {
    return null
  }

  const flutterHex = rawValue.match(/^0xff([0-9a-f]{6})$/i)
  if (flutterHex) {
    return `#${flutterHex[1]}`
  }

  const bareHex = rawValue.match(/^#?([0-9a-f]{6})$/i)
  if (bareHex) {
    return `#${bareHex[1]}`
  }

  return null
}

function readableForegroundForHex(hexColor: string): string {
  const normalized = normalizePrimaryColor(hexColor)
  if (!normalized) {
    return '#ffffff'
  }

  const red = Number.parseInt(normalized.slice(1, 3), 16)
  const green = Number.parseInt(normalized.slice(3, 5), 16)
  const blue = Number.parseInt(normalized.slice(5, 7), 16)
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255
  return luminance > 0.62 ? '#111827' : '#ffffff'
}

export function buildAuthThemeStyle(primaryColor: unknown): CSSProperties {
  const normalizedPrimary = normalizePrimaryColor(primaryColor)
  if (!normalizedPrimary) {
    return {}
  }

  const foreground = readableForegroundForHex(normalizedPrimary)
  return {
    '--primary': normalizedPrimary,
    '--primary-foreground': foreground,
    '--ring': normalizedPrimary,
    '--sidebar-primary': normalizedPrimary,
    '--sidebar-primary-foreground': foreground,
  } as CSSProperties
}

function hostMatchesFallback(host: string, fallback: ThemeFallbackConfig | null | undefined): boolean {
  const normalizedHosts = (fallback?.allowed_hosts ?? [])
    .map(normalizeHost)
    .filter(Boolean)

  return normalizedHosts.includes(host)
}

async function loadThemeFallbackConfig(): Promise<ThemeFallbackConfig | null> {
  try {
    const response = await fetch('/theme.json', { cache: 'no-store' })
    if (!response.ok) {
      return null
    }

    return await response.json() as ThemeFallbackConfig
  } catch {
    return null
  }
}

export function AuthThemeProvider({ children }: { children: ReactNode }) {
  const selectedBackendServerUrl = useSelectedBackendServerUrl()
  const host = useMemo(() => (
    typeof window === 'undefined' ? '' : normalizeHost(window.location.hostname)
  ), [])
  const shouldResolveDomain = Boolean(host) && !isLocalHost(host)

  const domainQuery = useQuery({
    queryKey: ['auth-theme-domain', selectedBackendServerUrl, host],
    queryFn: () => orgTreeApi.resolveTenantThemeByHost(host),
    enabled: shouldResolveDomain,
    staleTime: 60_000,
    retry: false,
  })

  const fallbackQuery = useQuery({
    queryKey: ['auth-theme-fallback', selectedBackendServerUrl, host],
    queryFn: loadThemeFallbackConfig,
    enabled: !shouldResolveDomain || domainQuery.isFetched,
    staleTime: 30_000,
    retry: false,
  })

  const fallbackOrganizationId = String(
    fallbackQuery.data?.organisation_id ?? fallbackQuery.data?.organization_id ?? '',
  ).trim()
  const canUseFallback = Boolean(fallbackQuery.data?.enabled && fallbackOrganizationId)
    && (isLocalHost(host) || hostMatchesFallback(host, fallbackQuery.data))

  const fallbackOrganizationQuery = useQuery({
    queryKey: ['auth-theme-fallback-organization', selectedBackendServerUrl, fallbackOrganizationId],
    queryFn: () => orgTreeApi.resolvePublicSignupServiceInstance(fallbackOrganizationId),
    enabled: canUseFallback,
    staleTime: 60_000,
    retry: false,
  })

  const state = useMemo<AuthThemeState>(() => {
    if (shouldResolveDomain && domainQuery.isLoading) {
      return {
        status: 'loading',
        source: 'none',
        host,
        locked: true,
        organization: null,
        primaryColor: null,
        message: null,
      }
    }

    if (domainQuery.data) {
      const resolvedDomain = normalizeHost(domainQuery.data.admin_domain)
      if (resolvedDomain !== host) {
        return {
          status: 'error',
          source: 'domain',
          host,
          locked: true,
          organization: null,
          primaryColor: null,
          message: 'The resolved organization does not match this domain.',
        }
      }

      return {
        status: 'ready',
        source: 'domain',
        host,
        locked: true,
        organization: domainQuery.data,
        primaryColor: normalizePrimaryColor(domainQuery.data.primary_color),
        message: null,
      }
    }

    if (fallbackQuery.isLoading) {
      return {
        status: 'loading',
        source: 'none',
        host,
        locked: shouldResolveDomain,
        organization: null,
        primaryColor: null,
        message: null,
      }
    }

    if (canUseFallback && fallbackOrganizationQuery.isLoading) {
      return {
        status: 'loading',
        source: 'fallback',
        host,
        locked: true,
        organization: null,
        primaryColor: normalizePrimaryColor(fallbackQuery.data?.primary_color),
        message: null,
      }
    }

    if (canUseFallback && fallbackOrganizationQuery.data) {
      return {
        status: 'ready',
        source: 'fallback',
        host,
        locked: true,
        organization: fallbackOrganizationQuery.data,
        primaryColor: normalizePrimaryColor(
          fallbackOrganizationQuery.data.primary_color || fallbackQuery.data?.primary_color,
        ),
        message: null,
      }
    }

    return {
      status: 'unconfigured',
      source: 'none',
      host,
      locked: false,
      organization: null,
      primaryColor: fallbackQuery.data?.enabled
        ? normalizePrimaryColor(fallbackQuery.data?.primary_color)
        : null,
      message: null,
    }
  }, [
    canUseFallback,
    domainQuery.data,
    domainQuery.isLoading,
    fallbackOrganizationQuery.data,
    fallbackOrganizationQuery.isLoading,
    fallbackQuery.data,
    fallbackQuery.isLoading,
    host,
    shouldResolveDomain,
  ])

  const style = useMemo(() => buildAuthThemeStyle(state.primaryColor), [state.primaryColor])

  if (state.status === 'error') {
    return (
      <AuthThemeContext.Provider value={state}>
        <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10 text-foreground" style={style}>
          <div className="w-full max-w-lg rounded-2xl border border-destructive/30 bg-card p-6 text-center shadow-sm">
            <h1 className="text-xl font-bold">Organization theme is not available</h1>
            <p className="mt-2 text-sm text-muted-foreground">{state.message}</p>
            {state.host ? (
              <p className="mt-4 rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
                Domain: <span className="font-medium text-foreground">{state.host}</span>
              </p>
            ) : null}
          </div>
        </div>
      </AuthThemeContext.Provider>
    )
  }

  if (state.status === 'loading' && state.locked) {
    return (
      <AuthThemeContext.Provider value={state}>
        <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10 text-foreground" style={style}>
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 text-center shadow-sm">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <p className="mt-4 text-sm text-muted-foreground">Loading organization theme...</p>
          </div>
        </div>
      </AuthThemeContext.Provider>
    )
  }

  return (
    <AuthThemeContext.Provider value={state}>
      <div style={style}>{children}</div>
    </AuthThemeContext.Provider>
  )
}

export function useAuthTheme() {
  return useContext(AuthThemeContext)
}

export function getAuthThemeServiceInstanceId(theme: Pick<AuthThemeState, 'organization'>): string {
  return String(theme.organization?.instance_id ?? theme.organization?.id ?? '').trim()
}

export function assertAuthThemeServiceInstanceMatch(theme: AuthThemeState, value: unknown): boolean {
  if (!theme.locked) {
    return true
  }

  const lockedId = getAuthThemeServiceInstanceId(theme)
  const candidate = String(value ?? '').trim()
  return Boolean(lockedId && candidate && lockedId === candidate)
}

